import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Dialog, Box, Typography, Button, IconButton } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import InsightsIcon from "@mui/icons-material/Insights";
import BarChartIcon from "@mui/icons-material/BarChart";
import TimelineIcon from "@mui/icons-material/Timeline";
import DownloadIcon from "@mui/icons-material/Download";
import { track } from "./analytics/analytics";

const INK = "#0B2A4A", BLUE = "#1E6FE8", MUTED = "#64748b", LINE = "#e3ebf3";

// One-time announcement for the new BI & Reporting feature. Shows exactly once per user
// and never again after they dismiss it — keyed in localStorage by their subId. Every
// interaction (shown / explored / dismissed) is tracked so it appears in the user journey.
const seenKey = (subId) => `crediarc_bi_announce_${subId || "anon"}`;
const onboardingKey = (subId) => `crediarc_onboarding_${subId || "anon"}`;

const FEATURES = [
  { icon: BarChartIcon, title: "Portfolio dashboards", desc: "Exposure, risk bands and limits at a glance." },
  { icon: TimelineIcon, title: "Trends over time", desc: "Track ARC scores and onboarding month over month." },
  { icon: DownloadIcon, title: "Exportable reports", desc: "Scheduled and on-demand reports for your team." },
];

export default function BIReportingAnnouncement() {
  const navigate = useNavigate();
  const subId = typeof window !== "undefined" ? localStorage.getItem("subId") : null;
  const [open, setOpen] = useState(false);
  const checkedRef = useRef(false);

  useEffect(() => {
    if (checkedRef.current || !subId) return;
    checkedRef.current = true;

    // Already shown once (or dismissed)? Never show again.
    if (localStorage.getItem(seenKey(subId))) return;
    // Don't stack on top of the first-run onboarding questionnaire — let new users finish
    // that first; they'll see this announcement on their next visit.
    if (localStorage.getItem(onboardingKey(subId)) !== "done") return;

    // Mark as seen the moment we decide to show it, so it appears exactly one time
    // even across reloads.
    localStorage.setItem(seenKey(subId), "seen");
    setOpen(true);
    track("bi_announcement_shown", { feature: "BI & Reporting" });
  }, [subId]);

  const dismiss = () => {
    localStorage.setItem(seenKey(subId), "dismissed");
    setOpen(false);
    track("bi_announcement_dismissed", { feature: "BI & Reporting" });
  };

  const explore = () => {
    localStorage.setItem(seenKey(subId), "cta");
    setOpen(false);
    track("bi_announcement_cta", { feature: "BI & Reporting", destination: "/reporting" });
    navigate("/reporting");
  };

  return (
    <Dialog
      open={open}
      onClose={dismiss}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: { borderRadius: "18px", overflow: "hidden" } }}
    >
      {/* Header band */}
      <Box sx={{ position: "relative", px: 4, pt: 4, pb: 3, background: `linear-gradient(135deg, ${INK} 0%, #123a63 100%)`, color: "#fff" }}>
        <IconButton
          aria-label="close"
          onClick={dismiss}
          sx={{ position: "absolute", top: 12, right: 12, color: "rgba(255,255,255,0.85)" }}
        >
          <CloseIcon />
        </IconButton>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 1.5 }}>
          <Box sx={{ display: "grid", placeItems: "center", width: 44, height: 44, borderRadius: "12px", background: "rgba(255,255,255,0.14)" }}>
            <InsightsIcon sx={{ fontSize: 26 }} />
          </Box>
          <Typography sx={{ fontSize: 12, fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase", opacity: 0.85 }}>
            New feature
          </Typography>
        </Box>
        <Typography sx={{ fontSize: 26, fontWeight: 800, lineHeight: 1.15 }}>
          BI &amp; Reporting is here
        </Typography>
        <Typography sx={{ mt: 1, fontSize: 15, opacity: 0.9, maxWidth: 440 }}>
          Turn your portfolio into clear, shareable insight — dashboards, trends and exportable reports, all in one place.
        </Typography>
      </Box>

      {/* Feature list */}
      <Box sx={{ px: 4, py: 3, display: "flex", flexDirection: "column", gap: 2 }}>
        {FEATURES.map(({ icon: Icon, title, desc }) => (
          <Box key={title} sx={{ display: "flex", gap: 2, alignItems: "flex-start" }}>
            <Box sx={{ display: "grid", placeItems: "center", width: 38, height: 38, borderRadius: "10px", background: "#eef4fe", color: BLUE, flexShrink: 0 }}>
              <Icon sx={{ fontSize: 20 }} />
            </Box>
            <Box>
              <Typography sx={{ fontSize: 15, fontWeight: 700, color: INK }}>{title}</Typography>
              <Typography sx={{ fontSize: 13.5, color: MUTED }}>{desc}</Typography>
            </Box>
          </Box>
        ))}
      </Box>

      {/* Actions */}
      <Box sx={{ px: 4, pb: 3.5, pt: 1, display: "flex", justifyContent: "flex-end", gap: 1.5, borderTop: `1px solid ${LINE}` }}>
        <Button onClick={dismiss} sx={{ textTransform: "none", color: MUTED, fontWeight: 600 }}>
          Maybe later
        </Button>
        <Button
          onClick={explore}
          variant="contained"
          sx={{ textTransform: "none", fontWeight: 700, borderRadius: "10px", px: 3, background: BLUE, "&:hover": { background: "#1a5fd0" } }}
        >
          Explore BI &amp; Reporting
        </Button>
      </Box>
    </Dialog>
  );
}
